import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ConfirmActionDialogComponent } from '../../calendar/confirm-action-dialog.component';
import { sportProfile, toSportKey } from '../../universal/constants/sports';
import {
  DeviceDuplicateGroup, DeviceDuplicateMember, DeviceDuplicateService, ResolveAction,
} from './device-duplicate.service';

export interface DeviceDuplicateDialogData {
  groups: DeviceDuplicateGroup[];
}

export interface DeviceDuplicateDialogResult {
  /** Wie viele Gruppen bearbeitet wurden (fuer den Banner-Refresh). */
  resolved: number;
}

/** Eine Zeile der Detailtabelle. */
interface MetricRow {
  labelKey: string;
  value: (m: DeviceDuplicateMember) => string;
  /** Nur eine Seite liefert den Wert -> die Zeile ist entscheidungsrelevant. */
  onlyOne: boolean;
}

/**
 * Doppelaufzeichnung aufloesen: dieselbe Einheit wurde von ZWEI Geraeten
 * aufgezeichnet (Uhr + Radcomputer) und zaehlt dadurch doppelt.
 *
 * Aufbau nach Prototyp 3 (docs/prototypes/device-duplicates/): zwei
 * Entscheidungs-Karten mit begruendeter Empfehlung, darunter die Spalten-Diff-
 * Tabelle aus Prototyp 1 als Ausklapper. Begruendung dieser Kombination: die
 * Frage lautet "welche soll zaehlen?", nicht "worin unterscheiden sie sich?" —
 * der Kennzahlenvergleich ist der Beleg, nicht die Hauptsache.
 *
 * Primaeraktion ist RUHIGSTELLEN, nicht Loeschen: verlustfrei und jederzeit
 * umkehrbar. Loeschen ist bewusst der zweite, bestaetigungspflichtige Weg.
 */
@Component({
  standalone: true,
  selector: 'app-device-duplicate-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule,
    MatIconModule, MatProgressSpinnerModule, MatSnackBarModule, MatTooltipModule,
    TranslateModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ 'training_analysis.device_duplicates.dialog.title' | translate }}</h2>

    <mat-dialog-content>
      @if (!rows.length) {
        <p class="dd-empty">{{ 'training_analysis.device_duplicates.dialog.empty' | translate }}</p>
      }

      @for (g of rows; track g.group_id) {
        <div class="dd-group" [class.dd-busy]="busy === g.group_id">
          <div class="dd-head">
            <mat-icon class="dd-head__icon">content_copy</mat-icon>
            <strong>{{ g.date }}</strong>
            <span class="dd-conf" [class.dd-conf--high]="g.confidence === 'high'">
              {{ (g.confidence === 'high'
                    ? 'training_analysis.device_duplicates.dialog.confidence_high'
                    : 'training_analysis.device_duplicates.dialog.confidence_medium') | translate }}
              @if (g.overlap_ratio != null) {
                · {{ 'training_analysis.device_duplicates.dialog.overlap'
                     | translate:{ value: overlapPct(g) } }}
              }
            </span>
          </div>

          <p class="dd-question">
            {{ 'training_analysis.device_duplicates.dialog.question' | translate }}
          </p>
          @if (g.partial) {
            <p class="dd-partial">
              <mat-icon>sync_alt</mat-icon>
              <span>{{ 'training_analysis.device_duplicates.dialog.partial_hint' | translate }}</span>
            </p>
          }

          <div class="dd-cards">
            @for (m of g.members; track m.session_id) {
              <button type="button" class="dd-card"
                      [attr.aria-pressed]="picked[g.group_id] === m.session_id"
                      [disabled]="locked(g, m)"
                      (click)="pick(g, m)">
                <div class="dd-dev">
                  <mat-icon>{{ deviceIcon(m) }}</mat-icon>
                  <div class="dd-dev__txt">
                    <span class="dd-dev__name">{{ m.device.label }}</span>
                    <span class="dd-dev__sub">
                      {{ startTime(m) }} · {{ duration(m) }} · {{ distance(m) }}
                    </span>
                  </div>
                  @if (picked[g.group_id] === m.session_id) {
                    <mat-icon class="dd-card__check">check_circle</mat-icon>
                  }
                </div>

                @if (m.protected) {
                  <div class="dd-protected">
                    <mat-icon>verified_user</mat-icon>
                    <span>{{ 'training_analysis.device_duplicates.dialog.protected' | translate }}</span>
                  </div>
                  @if (m.matched_leg; as leg) {
                    <div class="dd-leg">
                      {{ 'training_analysis.device_duplicates.dialog.matched_leg' | translate:{
                           sport: (legLabelKey(leg) | translate),
                           index: leg.leg_index + 1,
                           total: leg.legs_total } }}
                    </div>
                  }
                }

                <!-- Was kann diese Aufzeichnung, was die andere nicht kann?
                     Das ist das Kriterium, nach dem man tatsaechlich entscheidet. -->
                <ul class="dd-plus">
                  @for (f of features(m); track f.labelKey) {
                    <li [class.dd-yes]="f.ok" [class.dd-no]="!f.ok">
                      <mat-icon>{{ f.ok ? 'check' : 'remove' }}</mat-icon>
                      {{ f.labelKey | translate }}
                    </li>
                  }
                </ul>

                @if (recommendation(g, m); as rec) {
                  <div class="dd-reco" [class.dd-reco--neutral]="!rec.strong">
                    <mat-icon>{{ rec.strong ? 'recommend' : 'info' }}</mat-icon>
                    <span>{{ rec.text }}</span>
                  </div>
                }
              </button>
            }
          </div>

          <details class="dd-details">
            <summary>{{ 'training_analysis.device_duplicates.dialog.details' | translate }}</summary>
            <div class="dd-scroll">
              <table class="dd-diff">
                <thead>
                  <tr>
                    <th></th>
                    @for (m of g.members; track m.session_id) {
                      <th [class.dd-col-picked]="picked[g.group_id] === m.session_id">
                        {{ m.device.label }}
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of metricRows(g); track row.labelKey) {
                    <tr [class.dd-only]="row.onlyOne">
                      <td class="dd-lbl">{{ row.labelKey | translate }}</td>
                      @for (m of g.members; track m.session_id) {
                        <td class="dd-val"
                            [class.dd-col-picked]="picked[g.group_id] === m.session_id">
                          {{ row.value(m) }}
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </details>

          <div class="dd-actions">
            <button mat-flat-button color="primary"
                    [disabled]="busy !== null || !othersMayLose(g)"
                    (click)="resolve(g, 'mute')">
              <mat-icon>volume_off</mat-icon>
              {{ 'training_analysis.device_duplicates.dialog.action_mute' | translate }}
            </button>
            <button mat-stroked-button color="warn"
                    [disabled]="busy !== null || !othersMayLose(g)"
                    (click)="resolve(g, 'delete')">
              <mat-icon>delete</mat-icon>
              {{ 'training_analysis.device_duplicates.dialog.action_delete' | translate }}
            </button>
            <button mat-stroked-button [disabled]="busy !== null" (click)="resolve(g, 'dismiss')">
              {{ 'training_analysis.device_duplicates.dialog.action_dismiss' | translate }}
            </button>
            @if (busy === g.group_id) { <mat-spinner diameter="18"></mat-spinner> }
          </div>

          <p class="dd-hint">
            <mat-icon>lightbulb</mat-icon>
            <span>{{ 'training_analysis.device_duplicates.muted.hint' | translate }}</span>
          </p>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" (click)="close()">
        {{ 'calendar.close' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dd-empty { color: var(--text-secondary); }
    .dd-group {
      border: 1px solid var(--border-color);
      border-radius: var(--radius-sm);
      padding: 0.7rem 0.85rem;
      margin-bottom: 0.9rem;
    }
    .dd-group.dd-busy { opacity: 0.6; pointer-events: none; }
    .dd-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
    .dd-head__icon { color: var(--status-warning); }
    .dd-conf {
      font-size: var(--fs-xs, 11px); font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.04em; padding: 2px 8px; border-radius: 999px;
      background: var(--status-warning-bg); color: var(--status-warning-text);
    }
    .dd-conf--high { background: var(--status-success-bg); color: var(--status-success-text); }
    .dd-question { margin: 0.55rem 0 0.6rem; font-size: var(--fs-sm); color: var(--text-secondary); }
    .dd-partial {
      display: flex; align-items: flex-start; gap: 0.35rem; margin: 0 0 0.6rem;
      font-size: var(--fs-xs, 11.5px); color: var(--text-secondary);
    }
    .dd-partial mat-icon { font-size: 15px; width: 15px; height: 15px; }
    .dd-card:disabled { cursor: default; opacity: 0.85; }
    .dd-card:disabled:hover { border-color: var(--border-color); }
    .dd-protected {
      display: flex; align-items: center; gap: 0.3rem; margin-top: 0.45rem;
      font-size: var(--fs-xs, 11.5px); font-weight: 600; color: var(--color-primary);
    }
    .dd-protected mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .dd-leg { margin-top: 0.2rem; font-size: var(--fs-xs, 11.5px); color: var(--text-secondary); }

    .dd-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }
    .dd-card {
      position: relative; text-align: left; width: 100%; cursor: pointer;
      background: var(--surface); color: inherit; font: inherit;
      border: 2px solid var(--border-color); border-radius: var(--radius-sm);
      padding: 0.65rem 0.75rem; transition: border-color 0.15s;
    }
    .dd-card:hover { border-color: var(--color-primary-light, var(--color-primary)); }
    .dd-card[aria-pressed="true"] { border-color: var(--color-primary); }
    .dd-card__check { color: var(--color-primary); margin-left: auto; }
    .dd-dev { display: flex; align-items: center; gap: 0.45rem; min-width: 0; }
    .dd-dev__txt { display: flex; flex-direction: column; min-width: 0; }
    .dd-dev__name { font-weight: 600; font-size: var(--fs-sm); }
    .dd-dev__sub { font-size: var(--fs-xs, 11px); color: var(--text-secondary); }

    .dd-plus { list-style: none; margin: 0.5rem 0 0; padding: 0; font-size: var(--fs-xs, 11.5px); }
    .dd-plus li { display: flex; align-items: center; gap: 0.3rem; }
    .dd-plus mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .dd-yes { color: var(--status-success-text, var(--text-primary)); }
    .dd-no { color: var(--text-secondary); }

    .dd-reco {
      display: flex; align-items: flex-start; gap: 0.35rem; margin-top: 0.5rem;
      padding: 0.4rem 0.5rem; border-radius: var(--radius-sm);
      background: var(--status-success-bg); color: var(--status-success-text);
      font-size: var(--fs-xs, 11.5px);
    }
    .dd-reco--neutral { background: var(--surface-variant); color: var(--text-secondary); }
    .dd-reco mat-icon { font-size: 15px; width: 15px; height: 15px; }

    .dd-details { margin-top: 0.6rem; }
    .dd-details summary { cursor: pointer; font-size: var(--fs-sm); color: var(--text-secondary); }
    /* Breite Tabellen scrollen in sich, nie die Seite. */
    .dd-scroll { overflow-x: auto; margin-top: 0.4rem; }
    table.dd-diff { border-collapse: collapse; width: 100%; font-size: var(--fs-sm); }
    table.dd-diff th, table.dd-diff td {
      padding: 0.35rem 0.55rem; border-bottom: 1px solid var(--border-color);
      text-align: left; white-space: nowrap;
    }
    table.dd-diff thead th { font-size: var(--fs-xs, 11px); color: var(--text-secondary); }
    .dd-lbl { color: var(--text-secondary); }
    /* Zeilen, die nur EINE Seite liefert, entscheiden meistens. */
    tr.dd-only td { background: var(--surface-variant); }
    .dd-col-picked { background: var(--surface-hover, var(--surface-variant)); }

    .dd-actions {
      display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin-top: 0.7rem;
    }
    .dd-hint {
      display: flex; align-items: flex-start; gap: 0.35rem; margin: 0.55rem 0 0;
      font-size: var(--fs-xs, 11.5px); color: var(--text-secondary);
    }
    .dd-hint mat-icon { font-size: 15px; width: 15px; height: 15px; }

    @media (max-width: 640px) {
      .dd-cards { grid-template-columns: 1fr; }
    }
  `],
})
export class DeviceDuplicateDialogComponent {
  private readonly svc = inject(DeviceDuplicateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  rows: DeviceDuplicateGroup[];
  /** group_id -> gewaehlte session_id (die, die zaehlen soll). */
  picked: Record<number, number> = {};
  busy: number | null = null;
  private resolved = 0;

  constructor(
    readonly dialogRef: MatDialogRef<DeviceDuplicateDialogComponent, DeviceDuplicateDialogResult>,
    @Inject(MAT_DIALOG_DATA) data: DeviceDuplicateDialogData,
  ) {
    this.rows = [...(data.groups ?? [])];
    for (const g of this.rows) {
      this.picked[g.group_id] = this.defaultPick(g)?.session_id ?? g.members[0]?.session_id;
    }
  }

  /**
   * Vorauswahl = die vollstaendigere Aufzeichnung (mehr Messgroessen, bei
   * Gleichstand die laengere). Bewusst nur eine VORauswahl: entschieden wird
   * erst mit dem Klick auf eine Aktion.
   */
  private defaultPick(g: DeviceDuplicateGroup): DeviceDuplicateMember | null {
    // Multisport-Einheit: nicht Vorauswahl, sondern fest -- sie verliert nie.
    const fixed = g.members.find((m) => m.protected);
    if (fixed) return fixed;
    return [...g.members].sort((a, b) => {
      const d = this.signalCount(b) - this.signalCount(a);
      return d !== 0 ? d : (b.duration_s ?? 0) - (a.duration_s ?? 0);
    })[0] ?? null;
  }

  private signalCount(m: DeviceDuplicateMember): number {
    return (m.avg_power != null ? 1 : 0)
      + (m.avg_cadence != null ? 1 : 0)
      + (m.avg_hr != null ? 1 : 0);
  }

  pick(g: DeviceDuplicateGroup, m: DeviceDuplicateMember): void {
    if (this.locked(g, m)) return;
    this.picked[g.group_id] = m.session_id;
    this.cdr.markForCheck();
  }

  /**
   * Nicht wählbar: eine Aufzeichnung neben einer Multisport-Einheit. Die
   * Multisport-Einheit enthält mehr als die Radstrecke und zählt immer
   * (Nutzerentscheidung) — das Backend lehnt alles andere ab.
   */
  locked(g: DeviceDuplicateGroup, m: DeviceDuplicateMember): boolean {
    return !m.protected && g.members.some((x) => x.protected);
  }

  /** Ruhigstellen/Löschen nur, wenn kein geschütztes Mitglied verlöre. */
  othersMayLose(g: DeviceDuplicateGroup): boolean {
    const keep = this.picked[g.group_id];
    return !g.members.some((x) => x.session_id !== keep && x.protected);
  }

  legLabelKey(leg: { sport: string | null; sub_sport: string | null }): string {
    return sportProfile(toSportKey(leg.sport, leg.sub_sport)).label_key;
  }

  features(m: DeviceDuplicateMember): Array<{ labelKey: string; ok: boolean }> {
    return [
      { labelKey: 'training_analysis.device_duplicates.metric.avg_power', ok: m.avg_power != null },
      { labelKey: 'training_analysis.device_duplicates.metric.avg_cadence', ok: m.avg_cadence != null },
      { labelKey: 'training_analysis.device_duplicates.metric.avg_hr', ok: m.avg_hr != null },
    ];
  }

  recommendation(g: DeviceDuplicateGroup, m: DeviceDuplicateMember):
      { strong: boolean; text: string } | null {
    // Bei einer Multisport-Gruppe steht die Wahl fest; eine Empfehlung für die
    // andere Seite widerspräche dem gesperrten Kärtchen.
    if (g.partial) return null;
    const other = g.members.find((x) => x.session_id !== m.session_id);
    if (!other) return null;
    const mine = this.signalCount(m), theirs = this.signalCount(other);
    if (mine > theirs) {
      return { strong: true, text: this.translate.instant(
        'training_analysis.device_duplicates.dialog.reco_more_signals') };
    }
    if (mine === theirs && (m.duration_s ?? 0) > (other.duration_s ?? 0)) {
      return { strong: false, text: this.translate.instant(
        'training_analysis.device_duplicates.dialog.reco_longer') };
    }
    return null;
  }

  metricRows(g: DeviceDuplicateGroup): MetricRow[] {
    const defs: Array<[string, (m: DeviceDuplicateMember) => string, keyof DeviceDuplicateMember]> = [
      ['start', (m) => this.startTime(m), 'start'],
      ['duration', (m) => this.duration(m), 'duration_s'],
      ['distance', (m) => this.distance(m), 'distance_m'],
      ['avg_power', (m) => this.num(m.avg_power, 'W'), 'avg_power'],
      ['avg_hr', (m) => this.num(m.avg_hr, 'bpm'), 'avg_hr'],
      ['avg_cadence', (m) => this.num(m.avg_cadence, 'rpm'), 'avg_cadence'],
      ['training_load', (m) => this.num(m.training_load), 'training_load'],
      ['energy', (m) => this.num(m.energy_kj, 'kJ'), 'energy_kj'],
    ];
    return defs.map(([key, value, field]) => ({
      labelKey: `training_analysis.device_duplicates.metric.${key}`,
      value,
      // "Nur eine Seite hat den Wert" ist das staerkste Argument in der Tabelle.
      onlyOne: new Set(g.members.map((m) => m[field] == null)).size > 1,
    }));
  }

  // ── Formatierung ────────────────────────────────────────────────────────
  deviceIcon(m: DeviceDuplicateMember): string {
    return (m.device.manufacturer || '').toLowerCase().includes('wahoo')
      ? 'settings_remote' : 'watch';
  }

  startTime(m: DeviceDuplicateMember): string {
    if (!m.start) return this.missing();
    const d = new Date(m.start);
    return Number.isNaN(d.getTime())
      ? this.missing()
      : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  duration(m: DeviceDuplicateMember): string {
    const s = m.duration_s ?? m.summary_duration_s;
    if (!s) return this.missing();
    const t = Math.round(s);
    return `${Math.floor(t / 3600)}:${String(Math.floor((t % 3600) / 60)).padStart(2, '0')}:`
      + `${String(t % 60).padStart(2, '0')}`;
  }

  distance(m: DeviceDuplicateMember): string {
    const d = m.distance_m ?? m.summary_distance_m;
    return d ? `${(d / 1000).toFixed(2)} km` : this.missing();
  }

  overlapPct(g: DeviceDuplicateGroup): number {
    return Math.round((g.overlap_ratio ?? 0) * 100);
  }

  private num(v: number | null, unit?: string): string {
    if (v == null) return this.missing();
    return `${Math.round(v)}${unit ? ' ' + unit : ''}`;
  }

  private missing(): string {
    return this.translate.instant('training_analysis.device_duplicates.metric.missing');
  }

  // ── Aktionen ────────────────────────────────────────────────────────────
  resolve(g: DeviceDuplicateGroup, action: ResolveAction): void {
    if (this.busy !== null) return;
    if (action === 'delete') {
      this.confirmDelete(g);
      return;
    }
    this.send(g, action);
  }

  /**
   * Loeschen bekommt als EINZIGE Aktion eine Rueckfrage: es ist der einzige
   * unumkehrbare Weg. Der Dialogtext benennt ausdruecklich die verlustfreie
   * Alternative, damit niemand aus Versehen Daten wegwirft.
   */
  private confirmDelete(g: DeviceDuplicateGroup): void {
    const losers = g.members.filter((m) => m.session_id !== this.picked[g.group_id]);
    const label = losers.map((m) => m.device.label).join(', ');
    this.dialog.open(ConfirmActionDialogComponent, {
      width: '28rem',
      data: {
        title: this.translate.instant(
          'training_analysis.device_duplicates.dialog.delete_confirm_title'),
        message: this.translate.instant(
          'training_analysis.device_duplicates.dialog.delete_confirm_body', { device: label }),
        confirmLabel: this.translate.instant('calendar.delete_confirm_yes'),
        cancelLabel: this.translate.instant('calendar.delete_cancel'),
        tone: 'warn',
      },
    }).afterClosed().subscribe((ok) => {
      if (ok) this.send(g, 'delete');
    });
  }

  private send(g: DeviceDuplicateGroup, action: ResolveAction): void {
    this.busy = g.group_id;
    this.cdr.markForCheck();
    this.svc.resolve(g.group_id, action, this.picked[g.group_id]).subscribe({
      next: () => {
        this.rows = this.rows.filter((x) => x.group_id !== g.group_id);
        this.resolved += 1;
        this.busy = null;
        this.snackBar.open(this.translate.instant(
          `training_analysis.device_duplicates.dialog.saved_${action}`), 'OK', { duration: 4000 });
        this.cdr.markForCheck();
        if (!this.rows.length) this.close();
      },
      error: (err) => {
        this.busy = null;
        const reason = String(err?.error?.error ?? '');
        this.snackBar.open(this.translate.instant(reason.startsWith('multisport_not_mutable')
          ? 'training_analysis.device_duplicates.dialog.multisport_not_mutable'
          : 'training_analysis.device_duplicates.dialog.failed'), 'OK', { duration: 4000 });
        this.cdr.markForCheck();
      },
    });
  }

  close(): void {
    this.dialogRef.close({ resolved: this.resolved });
  }
}
